import { Link } from "react-router-dom";
import { ArrowRight, Award, BrainCircuit, Handshake } from "lucide-react";
import { FadeIn, MaskedLine, Crosshair, MonoLabel } from "../components/motion";
import EditorialMarquee from "../components/Marquee";

const MACHINE_IMG = "https://images.unsplash.com/photo-1763054763616-d2d2ed2312b8?q=80&w=1600&auto=format&fit=crop";
const SITE_IMG = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1600&auto=format&fit=crop";
const QUARRY_IMG = "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?q=80&w=1600&auto=format&fit=crop";

const VALUES = [
    { n: "01", icon: Award, title: "Quality", text: "Products selected for demanding construction and infrastructure applications." },
    { n: "02", icon: BrainCircuit, title: "Knowledge", text: "A technically minded approach to ground engineering products and applications." },
    { n: "03", icon: Handshake, title: "Reliability", text: "Professional service and dependable product supply." },
];

const About = () => (
    <div data-testid="about-page">
        <section className="relative border-b border-line overflow-hidden" data-testid="about-hero">
            <div className="absolute inset-0">
                <img src={SITE_IMG} alt="Civil construction site" className="w-full h-full object-cover grayscale" />
                <div className="absolute inset-0 bg-black/70" />
            </div>
            <Crosshair className="top-28 left-4 md:left-8 z-10" />
            <div className="relative px-4 md:px-8 pt-44 pb-16 md:pt-56 md:pb-24">
                <FadeIn delay={0.1}><MonoLabel>About / Solid Ground Solutions</MonoLabel></FadeIn>
                <h1 className="mt-6 font-heading font-black uppercase tracking-tighter leading-[0.9] text-[12vw] md:text-[8vw]" data-testid="about-headline">
                    <MaskedLine delay={0.2}>Experience.</MaskedLine>
                    <MaskedLine delay={0.32}>Knowledge.</MaskedLine>
                    <MaskedLine delay={0.44} className="text-[#FF5C00]">Solid Solutions.</MaskedLine>
                </h1>
            </div>
        </section>

        <EditorialMarquee items={["Every Site Is Different", "Ground Conditions Matter", "Right Product, Right Project"]} />

        <section className="border-b border-line" data-testid="about-story">
            <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative min-h-[320px] lg:min-h-0 overflow-hidden group border-b lg:border-b-0 lg:border-r border-line">
                    <img src={MACHINE_IMG} alt="Earthmoving machinery on site" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-black text-white px-3 py-1">FIG. 02 — Earthworks</span>
                </div>
                <div className="p-8 md:p-16 lg:p-24">
                    <FadeIn><MonoLabel>01 / Who We Are</MonoLabel></FadeIn>
                    <h2 className="mt-8 font-heading font-black uppercase tracking-tighter leading-[0.95] text-3xl md:text-5xl" data-testid="about-story-headline">
                        <MaskedLine inView>Every Project Has</MaskedLine>
                        <MaskedLine inView delay={0.1}>Different Ground.</MaskedLine>
                    </h2>
                    <FadeIn delay={0.2} className="mt-10 space-y-6 text-white/70 leading-relaxed text-base md:text-lg">
                        <p>
                            Solid Ground Solutions exists for one reason: ground conditions decide whether a project performs — and no two sites are the same. Different soils, different loads, different applications, different requirements.
                        </p>
                        <p>
                            We supply geotextiles, geogrids and engineered ground products with that reality in mind. Our job is to understand what your project is asking of the ground, then match it with products that are up to the task.
                        </p>
                    </FadeIn>
                </div>
            </div>
        </section>

        <section className="border-b border-line" data-testid="about-approach">
            <div className="px-8 md:px-16 lg:px-24 py-32 md:py-44">
                <FadeIn><MonoLabel>02 / Our Approach</MonoLabel></FadeIn>
                <h2 className="mt-8 font-heading font-black uppercase tracking-tighter leading-[0.95] text-[9vw] md:text-[5.5vw] max-w-6xl" data-testid="approach-headline">
                    <MaskedLine inView>We don't just</MaskedLine>
                    <MaskedLine inView delay={0.1}>supply products.</MaskedLine>
                    <MaskedLine inView delay={0.2} className="text-outline-orange">We help you find</MaskedLine>
                    <MaskedLine inView delay={0.3} className="text-outline-orange">the right solution.</MaskedLine>
                </h2>
                <FadeIn delay={0.35} className="mt-12 max-w-2xl">
                    <p className="text-white/70 leading-relaxed text-base md:text-lg">
                        We start with the application — the ground conditions, the loads, the design intent — and recommend products to suit. Not whatever happens to be on the shelf. That means fewer compromises on site and better long-term outcomes for the project.
                    </p>
                </FadeIn>
            </div>
        </section>

        <section className="border-b border-line" data-testid="about-values">
            <div className="p-8 md:p-16 lg:p-24 pb-12 md:pb-16">
                <FadeIn><MonoLabel>03 / What We Stand For</MonoLabel></FadeIn>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-line">
                {VALUES.map((v, i) => (
                    <FadeIn key={v.n} delay={i * 0.1} className="group relative p-8 md:p-12 border-b md:border-b-0 md:border-r border-line last:border-r-0 hover:bg-charcoal transition-colors duration-500">
                        <div className="flex items-start justify-between">
                            <v.icon className="w-6 h-6 text-[#FF5C00]" strokeWidth={1.5} />
                            <span className="font-mono text-xs text-white/30">{v.n}</span>
                        </div>
                        <h3 className="mt-16 md:mt-24 font-heading font-extrabold uppercase tracking-tight text-2xl md:text-3xl" data-testid={`value-${v.title.toLowerCase()}`}>{v.title}</h3>
                        <p className="mt-4 text-sm text-white/60 leading-relaxed">{v.text}</p>
                        <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#FF5C00] transition-[width] duration-500 group-hover:w-full" />
                    </FadeIn>
                ))}
            </div>
        </section>

        <section className="relative border-b border-line overflow-hidden">
            <div className="absolute inset-0">
                <img src={QUARRY_IMG} alt="Quarry and heavy equipment from above" className="w-full h-full object-cover grayscale" />
                <div className="absolute inset-0 bg-black/75" />
            </div>
            <div className="relative px-8 md:px-16 lg:px-24 py-28 md:py-40 flex flex-col md:flex-row md:items-end justify-between gap-10">
                <h2 className="font-heading font-black uppercase tracking-tighter leading-[0.9] text-[10vw] md:text-[5.5vw]" data-testid="about-cta-headline">
                    <MaskedLine inView>Talk to a team that</MaskedLine>
                    <MaskedLine inView delay={0.1}>knows the <span className="text-[#FF5C00]">ground.</span></MaskedLine>
                </h2>
                <FadeIn delay={0.2}>
                    <Link
                        to="/contact"
                        data-testid="about-contact-cta"
                        className="group inline-flex items-center justify-between gap-6 bg-[#FF5C00] text-white px-10 h-16 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300 shrink-0"
                    >
                        Contact Us
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </FadeIn>
            </div>
        </section>
    </div>
);

export default About;
